import {
  equipmentBoxId,
  equipmentTimerId,
  equipmentWaterSourceId,
  equipmentZoneId,
  problemEquipmentId,
  problemTimerId,
  problemWaterSourceId,
  problemZoneId,
} from '@lib/dto/irrigation.dto'
import type {
  IrrigationEquipmentDto,
  IrrigationProblemDto,
  IrrigationSiteDto,
  IrrigationTimerDto,
  WaterSourceDto,
  ZoneDto,
} from '@lib/dto/irrigation.dto'
import type { IrrigationProblemTypeDto } from '@lib/dto/irrigation-problem-type.dto'
import type { PlacedPoint } from '@lib/dto/geo.dto'
import type { IrrigationProblemSubject } from '@lib/enums/irrigation.enum'
import {
  lineSizeSchema,
  meterSizeSchema,
  valveSizeSchema,
} from '@lib/enums/irrigation.enum'
import type {
  IrrigationEntity,
  IrrigationEquipmentUpsert,
  IrrigationSyncOperation,
} from '@lib/input/irrigation.input'
import type { IrrigationRuleState } from '@lib/helpers/irrigation'

/**
 * One edit the survey makes: a whole record that should now look like this,
 * or a record that should be gone. The same intention shape the queue holds,
 * expressed in the records the screen already has.
 */
export type IrrigationChange =
  | { kind: 'upsert'; entity: 'waterSource'; record: WaterSourceDto }
  | { kind: 'upsert'; entity: 'timer'; record: IrrigationTimerDto }
  | { kind: 'upsert'; entity: 'zone'; record: ZoneDto }
  | { kind: 'upsert'; entity: 'equipment'; record: IrrigationEquipmentDto }
  | { kind: 'upsert'; entity: 'problem'; record: IrrigationProblemDto }
  | { kind: 'archive'; entity: IrrigationEntity; id: string }

/** The list in a site that holds an entity's records. `visitId` is not one. */
const LIST_FOR: Record<
  IrrigationEntity,
  keyof Omit<IrrigationSiteDto, 'siteId' | 'visitId'>
> = {
  waterSource: 'waterSources',
  timer: 'timers',
  zone: 'zones',
  equipment: 'equipment',
  problem: 'problems',
}

function replaceById<T extends { id: string }>(
  list: readonly T[],
  record: T,
): T[] {
  return list.some((item) => item.id === record.id)
    ? list.map((item) => (item.id === record.id ? record : item))
    : [...list, record]
}

/** The site as the screen should draw it once a change is made. */
export function applyChange(
  site: IrrigationSiteDto,
  change: IrrigationChange,
): IrrigationSiteDto {
  if (change.kind === 'archive') {
    const key = LIST_FOR[change.entity]
    return {
      ...site,
      waterSources:
        key === 'waterSources'
          ? site.waterSources.filter((item) => item.id !== change.id)
          : site.waterSources,
      timers:
        key === 'timers'
          ? site.timers.filter((item) => item.id !== change.id)
          : site.timers,
      zones:
        key === 'zones'
          ? site.zones.filter((item) => item.id !== change.id)
          : site.zones,
      equipment:
        key === 'equipment'
          ? site.equipment.filter((item) => item.id !== change.id)
          : site.equipment,
      problems:
        key === 'problems'
          ? site.problems.filter((item) => item.id !== change.id)
          : site.problems,
    }
  }
  switch (change.entity) {
    case 'waterSource':
      return {
        ...site,
        waterSources: replaceById(site.waterSources, change.record),
      }
    case 'timer':
      return { ...site, timers: replaceById(site.timers, change.record) }
    case 'zone':
      return { ...site, zones: replaceById(site.zones, change.record) }
    case 'equipment':
      return { ...site, equipment: replaceById(site.equipment, change.record) }
    case 'problem':
      return { ...site, problems: replaceById(site.problems, change.record) }
  }
}

function roundTo(value: number, places: number): number {
  const factor = 10 ** places
  return Math.round(value * factor) / factor
}

/**
 * A decimal string in the form the server stores and sends back ("1.5" for
 * "1.50"). Anything that is not a number is passed through for the input
 * schema to refuse with its own message.
 */
function canonicalDecimal(value: string | null): string | null {
  if (value === null || value.trim() === '') return value
  const number = Number(value)
  return Number.isFinite(number) ? String(number) : value
}

/**
 * A zone's outline at the precision of its pins. The outline is stored as
 * JSON, and the database hands numbers back with about fifteen significant
 * digits, so a corner tapped at 33.519868943812696 comes back as
 * 33.5198689438127. Rounding to six places (about 0.11 m, finer than a tap)
 * before sending means the saved sheet and the server's copy compare equal,
 * the reason `pinOf` rounds.
 */
function outlineOf(boundary: ZoneDto['boundary']): ZoneDto['boundary'] {
  if (boundary === null) return null
  return {
    vertices: boundary.vertices.map((vertex) => ({
      latitude: roundTo(vertex.latitude, 6),
      longitude: roundTo(vertex.longitude, 6),
    })),
  }
}

/**
 * A record's pin, in the shape every upsert takes it, at the precision the
 * columns store: six places of a degree (about 0.11 m) and two of a meter.
 * Sending exactly what will be stored is what lets a saved draft and the
 * server's copy of it compare equal.
 */
function pinOf(record: PlacedPoint) {
  return {
    latitude: roundTo(record.latitude, 6),
    longitude: roundTo(record.longitude, 6),
    accuracyM: record.accuracyM === null ? null : roundTo(record.accuracyM, 2),
    positionSource: record.positionSource,
  }
}

/**
 * A piece of equipment as its kind's upsert, carrying only that kind's
 * fields. The input refuses a field from another kind, so building it from
 * the flat record has to leave the others out rather than send them as null.
 *
 * A size the record holds that this kind does not offer is sent as null
 * rather than refused: it can only have come from an older build, and losing
 * a size is better than wedging the queue.
 */
export function toEquipmentUpsert(
  record: IrrigationEquipmentDto,
): IrrigationEquipmentUpsert | null {
  const base = {
    id: record.id,
    ...pinOf(record),
    partUuid: record.part?.uuid ?? null,
    notes: record.notes,
  }
  switch (record.kind) {
    case 'VALVE': {
      const size = valveSizeSchema.safeParse(record.size)
      return {
        kind: 'VALVE',
        ...base,
        boxId: record.boxId,
        zoneId: record.zoneId,
        size: size.success ? size.data : null,
        decoderUuid: record.decoder?.uuid ?? null,
      }
    }
    case 'MASTER_VALVE': {
      const size = valveSizeSchema.safeParse(record.size)
      return {
        kind: 'MASTER_VALVE',
        ...base,
        boxId: record.boxId,
        timerId: record.timerId,
        waterSourceId: record.waterSourceId,
        size: size.success ? size.data : null,
        decoderUuid: record.decoder?.uuid ?? null,
      }
    }
    case 'BACKFLOW': {
      const size = lineSizeSchema.safeParse(record.size)
      return {
        kind: 'BACKFLOW',
        ...base,
        waterSourceId: record.waterSourceId,
        backflowType: record.backflowType,
        size: size.success ? size.data : null,
        serialNumber: record.serialNumber,
        lastTestedOn: record.lastTestedOn,
      }
    }
    case 'BALL_VALVE': {
      const size = lineSizeSchema.safeParse(record.size)
      return {
        kind: 'BALL_VALVE',
        ...base,
        boxId: record.boxId,
        waterSourceId: record.waterSourceId,
        size: size.success ? size.data : null,
      }
    }
    case 'SENSOR':
      return {
        kind: 'SENSOR',
        ...base,
        boxId: record.boxId,
        timerId: record.timerId,
        sensorType: record.sensorType,
      }
    case 'WELL_COMPONENT': {
      // The input requires a well and a type. A missing one is not yet an
      // upsert: an empty id is not a well, and "Other" would store a type
      // nobody chose.
      if (record.waterSourceId === null || record.wellComponentType === null) {
        return null
      }
      return {
        kind: 'WELL_COMPONENT',
        ...base,
        waterSourceId: record.waterSourceId,
        wellComponentType: record.wellComponentType,
      }
    }
    case 'VALVE_BOX':
      return { kind: 'VALVE_BOX', ...base, spliceCount: record.spliceCount }
    case 'OTHER':
      return {
        kind: 'OTHER',
        ...base,
        boxId: record.boxId,
        name: record.name ?? '',
      }
  }
}

/**
 * Why a change cannot be sent yet.
 *
 * Only a well component reaches this: every other upsert can be built and
 * then refused by the input schema, which carries its own message.
 */
function unsendableMessage(change: IrrigationChange): string {
  if (
    change.kind === 'upsert' &&
    change.entity === 'equipment' &&
    change.record.kind === 'WELL_COMPONENT'
  ) {
    if (change.record.wellComponentType === null) {
      return 'Say what the component is.'
    }
    if (change.record.waterSourceId === null) {
      return 'Say which well this is part of.'
    }
  }
  return 'That is not complete yet.'
}

/**
 * The fields a not-yet-sendable well component would have sent, so two edits
 * of one still compare as different. Pins are rounded the way a real upsert
 * rounds them, or a coordinate the database hands back would look edited.
 */
function unsendableFingerprint(change: IrrigationChange): string {
  if (
    change.kind !== 'upsert' ||
    change.entity !== 'equipment' ||
    change.record.kind !== 'WELL_COMPONENT'
  ) {
    return JSON.stringify(change)
  }
  const { record } = change
  return JSON.stringify({
    op: 'upsertEquipment',
    equipment: {
      kind: 'WELL_COMPONENT',
      id: record.id,
      ...pinOf(record),
      partUuid: record.part?.uuid ?? null,
      notes: record.notes,
      waterSourceId: record.waterSourceId,
      wellComponentType: record.wellComponentType,
    },
  })
}

/** An operation the input schema can accept, or why this change is not one yet. */
export type OperationBuild =
  | { ok: true; operation: IrrigationSyncOperation }
  | { ok: false; message: string }

/** The queued operation a change becomes, when it is complete enough to send. */
function buildOperation(
  change: IrrigationChange,
): IrrigationSyncOperation | null {
  if (change.kind === 'archive') {
    return { op: 'archive', entity: change.entity, id: change.id }
  }
  switch (change.entity) {
    case 'waterSource': {
      const { record } = change
      return {
        op: 'upsertWaterSource',
        waterSource: {
          id: record.id,
          kind: record.kind,
          label: record.label,
          ...pinOf(record),
          // A meter has a size and no pump; a well the other way round. The
          // input refuses the wrong one, so it is left out rather than sent.
          meterSize: record.kind === 'CITY_METER' ? meterSizeOf(record) : null,
          staticPsi: record.staticPsi,
          availableGpm: canonicalDecimal(record.availableGpm),
          horsepower:
            record.kind === 'WELL' ? canonicalDecimal(record.horsepower) : null,
          absentEquipment: record.absentEquipment,
          notes: record.notes,
        },
      }
    }
    case 'timer': {
      const { record } = change
      return {
        op: 'upsertTimer',
        timer: {
          id: record.id,
          label: record.label,
          ...pinOf(record),
          partUuid: record.part?.uuid ?? null,
          waterSourceId: record.waterSourceId,
          absentEquipment: record.absentEquipment,
          notes: record.notes,
        },
      }
    }
    case 'zone': {
      const { record } = change
      return {
        op: 'upsertZone',
        zone: {
          id: record.id,
          timerId: record.timerId,
          number: record.number,
          boundary: outlineOf(record.boundary),
          headCounts: record.headCounts,
          notes: record.notes,
        },
      }
    }
    case 'equipment': {
      const equipment = toEquipmentUpsert(change.record)
      if (equipment === null) return null
      return { op: 'upsertEquipment', equipment }
    }
    case 'problem': {
      const { record } = change
      return {
        op: 'upsertProblem',
        problem: {
          id: record.id,
          typeId: record.typeId,
          subject: record.subject,
          position: record.position === null ? null : pinOf(record.position),
          count: record.count,
          severity: record.severity,
          notes: record.notes,
          laborMinutes: record.laborMinutes,
          materials: record.materials.map((item) => ({
            materialUuid: item.material.uuid,
            quantity: canonicalDecimal(item.quantity) ?? item.quantity,
          })),
          previousId: record.previousId,
        },
      }
    }
  }
}

/** The queued operation a change becomes. */
export function toOperation(change: IrrigationChange): OperationBuild {
  const operation = buildOperation(change)
  if (operation === null) {
    return { ok: false, message: unsendableMessage(change) }
  }
  return { ok: true, operation }
}

/**
 * What a sheet compares to see whether it was edited.
 *
 * A complete change compares as the operation that would be sent. A well
 * component that cannot be sent yet still compares its own fields, so a note
 * typed on it is not lost behind the refusal.
 */
export function operationFingerprint(change: IrrigationChange): string {
  const operation = buildOperation(change)
  if (operation !== null) return JSON.stringify(operation)
  return unsendableFingerprint(change)
}

/** A meter's size, if it is one a meter comes in. */
function meterSizeOf(record: Extract<WaterSourceDto, { kind: 'CITY_METER' }>) {
  const size = meterSizeSchema.safeParse(record.meterSize)
  return size.success ? size.data : null
}

/** The parts of the working set the shared rules read. */
export function ruleStateOf(site: IrrigationSiteDto): IrrigationRuleState {
  return {
    waterSources: site.waterSources.map((item) => ({
      id: item.id,
      kind: item.kind,
    })),
    timers: site.timers.map((item) => ({
      id: item.id,
      waterSourceId: item.waterSourceId,
    })),
    zones: site.zones.map((item) => ({
      id: item.id,
      timerId: item.timerId,
      number: item.number,
    })),
    equipment: site.equipment.map((item) => ({
      id: item.id,
      kind: item.kind,
      zoneId: equipmentZoneId(item),
      timerId: equipmentTimerId(item),
      waterSourceId: equipmentWaterSourceId(item),
      boxId: equipmentBoxId(item),
    })),
    problems: site.problems.map((item) => ({
      id: item.id,
      typeId: item.typeId,
      zoneId: problemZoneId(item),
      timerId: problemTimerId(item),
      equipmentId: problemEquipmentId(item),
      waterSourceId: problemWaterSourceId(item),
      hasPosition: item.position !== null,
      isOpen: item.fixedAt === null,
    })),
  }
}

/** What each problem type applies to, archived ones included, as the rules read it. */
export function appliesToOf(
  problemTypes: readonly Pick<IrrigationProblemTypeDto, 'id' | 'appliesTo'>[],
): Map<string, readonly IrrigationProblemSubject[]> {
  return new Map(problemTypes.map((type) => [type.id, type.appliesTo]))
}

/** The key an operation collapses on in the queue: one intention per record. */
export function operationKey(operation: IrrigationSyncOperation): string {
  switch (operation.op) {
    case 'upsertWaterSource':
      return `waterSource:${operation.waterSource.id}`
    case 'upsertTimer':
      return `timer:${operation.timer.id}`
    case 'upsertZone':
      return `zone:${operation.zone.id}`
    case 'upsertEquipment':
      return `equipment:${operation.equipment.id}`
    case 'upsertProblem':
      return `problem:${operation.problem.id}`
    case 'archive':
      return `${operation.entity}:${operation.id}`
  }
}
